import React, {
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button, { IconButton, LinkButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/glyph/add';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Grid, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	AnalyticsContextWithSourceAndProperties,
	ExperienceFailure,
	ExperienceName,
	ExperienceSuccess,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import { encodeRovoParams } from '@atlassian/conversation-assistant-pubsub';
import { type Agent } from '@atlassian/conversation-assistant-service';
import { useProfileController, useUserPreferences } from '@atlassian/conversation-assistant-store';
import {
	ChatGenericError,
	SearchInput,
	StarIconButton,
} from '@atlassian/conversation-assistant-ui-components';

import { getAtlasURL } from '../../common/utils/atlas-url';
import { useInViewport } from '../../common/utils/use-is-in-viewport';
import { isCustomerAgent, useAgentsController } from '../../controllers/agents';
import { AgentCreatedBy, filterAndSortAgents } from '../../utils/agents';

import messages from './messages';
import { NoAgentsIllustration } from './no-agents-illustration';
import { type BrowseAgentsModalProps, type BrowseCategory } from './types';
import { ViewAgentModal } from './view-agent';

const gridStyles = xcss({
	marginBottom: 'space.300',
	width: '100%',
});

const wrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	paddingInline: 'space.300',
	height: '692px',
});

const browseWrapperStyles = xcss({
	alignItems: 'center',
	minHeight: '50vh',
});

const searchWrapperStyles = xcss({
	paddingBlockEnd: 'space.300',
	width: '100%',
});

const emptyStateContainerStyles = xcss({
	width: '600px',
	textAlign: 'center',
	paddingInline: 'space.500',
});

const baseButtonStyles = xcss({
	borderRadius: 'border.radius.400',
	display: 'inline-flex',
	boxSizing: 'border-box',
	width: 'auto',
	maxWidth: '100%',
	position: 'relative',
	alignItems: 'baseline',
	justifyContent: 'center',
	columnGap: 'space.050',
	borderWidth: 'border.width.0',
	flexShrink: 0,
	paddingInlineEnd: 'space.150',
	paddingInlineStart: 'space.150',
	textAlign: 'center',
	transition: 'background 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
	verticalAlign: 'middle',
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	paddingBlock: 'space.075',
	background: token('color.background.neutral.subtle', 'transparent'),
	// @ts-expect-error
	color: token('color.text', '#42526E'),
	':visited': {
		// @ts-expect-error
		color: token('color.text', '#42526E'),
	},
});

const selectedButtonStyles = xcss({
	background: token('color.background.selected'),
	// @ts-expect-error
	color: token('color.text.selected'),
	':visited': {
		// @ts-expect-error
		color: token('color.text.selected'),
	},
});

const headingStyles = xcss({ font: token('font.heading.medium') });

const BrowseAgentsModalInternal = ({
	onClose,
	siteId,
	onClickChat,
	handleClickConversationStarter,
}: BrowseAgentsModalProps) => {
	di(useAgentsController);
	di(useProfileController);
	di(useUserPreferences);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { availableAgents, favouritedAgents } = useAgentsController();
	const [selectedTab, setSelectedTab] = useState<BrowseCategory>('all-agents');
	const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
	const [searchValue, setSearchValue] = useState<string>('');
	const inputRef = useRef<HTMLInputElement>(null);
	const agentTabs: Array<{ label: string; state: BrowseCategory }> = [
		{ label: formatMessage(messages.allAgentsButton), state: 'all-agents' },
		{ label: formatMessage(messages.starredAgentsButton), state: 'starred-agents' },
		{ label: formatMessage(messages.myAgentsButton), state: 'my-agents' },
	];
	const profileData = useProfileController();
	const userId = profileData.aaid;
	const isAgentCreatedByUser = useCallback(
		(agent: Agent) => {
			return !!(userId && isCustomerAgent(agent) && agent.creator?.includes(userId));
		},
		[userId],
	);
	const { deleteAgent } = useAgentsController();

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'browseAgentsModal',
		});
	}, [sendAnalyticsEvent]);

	const agents = useMemo(() => {
		switch (selectedTab) {
			case 'all-agents':
				return filterAndSortAgents(availableAgents, { searchValue });
			case 'starred-agents':
				return filterAndSortAgents(favouritedAgents, { searchValue });
			case 'my-agents':
				const sortedAvailableAgents = filterAndSortAgents(availableAgents, { searchValue });
				return sortedAvailableAgents.filter((agent) => isAgentCreatedByUser(agent));
			default:
				return [];
		}
	}, [selectedTab, availableAgents, favouritedAgents, isAgentCreatedByUser, searchValue]);

	const hasCreatedAnyAgents = useMemo(
		() => availableAgents.some((agent) => isAgentCreatedByUser(agent)),
		[availableAgents, isAgentCreatedByUser],
	);

	const handleSearchChange = useCallback(
		(searchValue: string) => {
			setSearchValue(searchValue);
			sendAnalyticsEvent({
				action: 'searched',
				actionSubject: 'input',
				actionSubjectId: 'browseAgentsSearchInput',
				attributes: {
					searchValueLength: searchValue.length,
				},
			});
		},
		[setSearchValue, sendAnalyticsEvent],
	);

	const handleTabClick = ({
		agentTabState,
		analyticsEvent,
	}: {
		agentTabState: BrowseCategory;
		analyticsEvent: UIAnalyticsEvent;
	}) => {
		setSelectedTab(agentTabState);
		setSearchValue('');
		if (inputRef.current) {
			inputRef.current.value = '';
		}
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'browseAgentsFilterButton',
				attributes: {
					browseCategory: agentTabState,
				},
			}),
		);
	};

	const handleCloseModal = (analyticsEvent: UIAnalyticsEvent) => {
		onClose();
		sendAnalyticsEvent(
			analyticsEvent.update({
				actionSubjectId: 'browseAgentsCloseModalButton',
			}),
		);
	};

	const onEditAgent = (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => {
		e.stopPropagation();
		window.open(
			getAtlasURL(siteId, `/chat/agents/${agentId}/edit`),
			'_blank',
			'noopener, noreferrer',
		);
	};

	const onCopyAgent = (e: any, agentId: string) => {
		e.stopPropagation();
		const rovoParams = encodeRovoParams({
			pathway: 'chat',
			agentId: agentId,
		});
		navigator.clipboard.writeText(getAtlasURL(siteId, `/chat?${rovoParams}`));
		// Cheap way to update node text which will reset when it's next mounted
		e.target.innerHTML = formatMessage(messages.linkedCopiedToProfile);
	};

	const onDuplicateAgent = (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => {
		e.stopPropagation();
		const rovoParams = encodeRovoParams({
			pathway: 'agents-create',
			agentId: agentId,
		});
		window.open(
			// TODO remove fromAgentId when atlas supports rovoChat
			getAtlasURL(siteId, `/chat/agents/new?fromAgentId=${agentId}&${rovoParams}`),
			'_blank',
			'noopener, noreferrer',
		);
	};

	const onDeleteAgent = async (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => {
		e.stopPropagation();
		await deleteAgent(agentId);
	};

	return selectedAgent ? (
		<ViewAgentModal
			agent={selectedAgent}
			siteId={siteId}
			onGoBack={() => setSelectedAgent(null)}
			onClose={onClose}
			onClickChat={onClickChat}
			handleClickConversationStarter={handleClickConversationStarter}
			isAgentCreatedByUser={isAgentCreatedByUser(selectedAgent)}
			onEditAgent={onEditAgent}
			onCopyAgent={onCopyAgent}
			onDuplicateAgent={onDuplicateAgent}
			onDeleteAgent={onDeleteAgent}
		/>
	) : (
		<AnalyticsContextWithSourceAndProperties source="AIMate">
			<Modal testId="browse-agents-modal" width="x-large">
				<ModalHeader>
					<Inline space="space.200" alignBlock="center">
						<ModalTitle>{formatMessage(messages.modalTitle)}</ModalTitle>
						<Inline space="space.100">
							{agentTabs.map((agentTab, i) => (
								<Pressable
									key={i}
									testId="browse-agents-agent-tab"
									onClick={(_, analyticsEvent) =>
										handleTabClick({ agentTabState: agentTab.state, analyticsEvent })
									}
									xcss={[baseButtonStyles, selectedTab === agentTab.state && selectedButtonStyles]}
								>
									{agentTab.label}
								</Pressable>
							))}
						</Inline>
						<LinkButton
							iconBefore={AddIcon}
							appearance="primary"
							href={getAtlasURL(siteId, '/chat/agents/new')}
							target="_blank"
							onClick={(_, analyticsEvent) => {
								sendAnalyticsEvent(
									analyticsEvent.update({
										actionSubjectId: 'browseAgentsCreateAgentButton',
									}),
								);
							}}
						>
							{formatMessage(messages.createAgentButton)}
						</LinkButton>
					</Inline>
					<Button
						appearance="subtle"
						onClick={(_, analyticsEvent) => handleCloseModal(analyticsEvent)}
					>
						{formatMessage(messages.closeButton)}
					</Button>
				</ModalHeader>
				<ModalBody>
					{!hasCreatedAnyAgents && selectedTab === 'my-agents' ? (
						<Box xcss={wrapperStyles}>
							<Stack xcss={emptyStateContainerStyles}>
								<NoAgentsIllustration />
								<Stack space="space.500">
									<Stack space="space.100">
										<Box as="h3" xcss={headingStyles}>
											{formatMessage(messages.noCreatedAgentsHeader)}
										</Box>
										<p>{formatMessage(messages.noCreatedAgents)}</p>
									</Stack>
									<Inline alignInline="center" space="space.150">
										<LinkButton
											appearance="primary"
											href={getAtlasURL(siteId, '/chat/agents/new')}
											target="_blank"
											iconBefore={AddIcon}
										>
											{formatMessage(messages.createAnAgentButton)}
										</LinkButton>
										<LinkButton target="_blank" appearance="subtle" href="CHANGE">
											{formatMessage(messages.learnMore)}
										</LinkButton>
									</Inline>
								</Stack>
							</Stack>
						</Box>
					) : (
						<Stack xcss={browseWrapperStyles} alignBlock="start">
							<Box xcss={searchWrapperStyles}>
								<SearchInput
									placeholderText={formatMessage(messages.searchPlaceholder)}
									onSearch={handleSearchChange}
									ref={inputRef}
								/>
							</Box>
							<Grid templateColumns="1fr 1fr" gap="space.200" xcss={gridStyles}>
								{agents.length > 0 ? (
									agents.map((agent) => (
										<AgentCard
											key={agent.id}
											agent={agent}
											siteId={siteId}
											onViewAgent={() => setSelectedAgent(agent)}
											agentIsCreatedByUser={isAgentCreatedByUser(agent)}
											onClickChat={onClickChat}
											onEditAgent={onEditAgent}
											onCopyAgent={onCopyAgent}
											onDuplicateAgent={onDuplicateAgent}
											onDeleteAgent={onDeleteAgent}
										/>
									))
								) : (
									<Text color="color.text.subtle">{formatMessage(messages.noResults)}</Text>
								)}
								<ExperienceSuccess name={ExperienceName.LOAD_BROWSE_AGENTS} />
							</Grid>
						</Stack>
					)}
				</ModalBody>
			</Modal>
		</AnalyticsContextWithSourceAndProperties>
	);
};

const ErrorFallbackComponent = ({ error }: FallbackProps) => (
	<Box xcss={wrapperStyles}>
		<ChatGenericError errorType="generic" />
		<ExperienceFailure error={error} name={ExperienceName.LOAD_BROWSE_AGENTS} />
	</Box>
);

export const BrowseAgentsModal = (props: BrowseAgentsModalProps) => (
	<ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
		<BrowseAgentsModalInternal {...props} />
	</ErrorBoundary>
);

const cardContainerStyles = xcss({
	minHeight: '96px',
});

const cardContentStyles = xcss({
	background: token('elevation.surface'),
	border: `1px solid ${token('color.border')}`,
	borderRadius: 'border.radius.200',
	padding: 'space.200',
	display: 'flex',
	cursor: 'pointer',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	textAlign: 'left',
	position: 'relative',
	height: '100%',
});

const toolbarStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	zIndex: 'card',
});

const pressableAgentCardStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	left: 'space.0',
	right: 'space.0',
	bottom: 'space.0',
	borderRadius: 'border.radius.200',
	background: 'transparent',
	zIndex: 'card',
});

const clickableItemStyles = xcss({
	zIndex: 'card',
	display: 'flex',
	alignItems: 'center',
});

const createdByTextStyles = xcss({
	paddingRight: 'space.050',
});

const dropdownIconButtonWrapper = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	height: '32px',
	width: '32px',
});

const hiddenStyles = xcss({
	opacity: 0,
});

type AgentCardProps = {
	agent: Agent;
	siteId: string;
	onViewAgent: (agent: Agent) => void;
	agentIsCreatedByUser: boolean;
	onClickChat: (agent: Agent) => void;
	onEditAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
	onCopyAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
	onDuplicateAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
	onDeleteAgent: (
		e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>,
		agentId: string,
	) => void;
};

const AgentCard = (props: AgentCardProps) => {
	const containerRef = useRef(null);
	const { hasBeenInViewport } = useInViewport({
		ref: containerRef,
	});
	const renderCardContent =
		!fg('rovo_chat_lazy_render_agents') ||
		(hasBeenInViewport && fg('rovo_chat_lazy_render_agents'));

	return (
		<Box ref={containerRef} xcss={cardContainerStyles}>
			{renderCardContent && <AgentCardContent {...props} />}
		</Box>
	);
};

const AgentCardContent = ({
	agent,
	siteId,
	onViewAgent,
	onEditAgent,
	onCopyAgent,
	onDuplicateAgent,
	onDeleteAgent,
	agentIsCreatedByUser,
	onClickChat,
}: AgentCardProps) => {
	di(useUserPreferences);

	const [isFocusedOrHovered, setIsFocusedOrHovered] = useState(false);
	const { sendAnalyticsEvent } = useAnalytics();
	const isBEFavouritingAgentsEnabled = fg('rovo_chat_favourite_agent_via_backend');

	let hoveredFalseTimeout = useRef<number | null>(null);
	const setIsHoveredFalseWithTimeout = useCallback(() => {
		if (hoveredFalseTimeout.current) {
			clearTimeout(hoveredFalseTimeout.current);
		}
		hoveredFalseTimeout.current = window.setTimeout(() => {
			setIsFocusedOrHovered(false);
			setIsDropdownOpen(false);
		}, 0);
	}, []);

	const setIsHoveredTrue = useCallback(() => {
		if (hoveredFalseTimeout.current) {
			clearTimeout(hoveredFalseTimeout.current);
		}
		setIsFocusedOrHovered(true);
	}, []);

	const [isDropdownOpen, setIsDropdownOpen] = useState(false);

	const { formatMessage } = useIntl();
	const { setFavouriteAgent: setFavouriteAgentViaPrefs, isFavouriteAgent } = useUserPreferences();
	const isStarred = isBEFavouritingAgentsEnabled ? agent.favourite : isFavouriteAgent(agent.id);
	const { setFavouriteAgent } = useAgentsController();

	const handleViewAgent = useCallback(
		(e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>) => {
			e.stopPropagation();
			onViewAgent(agent);
			setIsDropdownOpen(!isDropdownOpen);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgentsViewAgentOption',
				attributes: {
					agentId: agent.id,
				},
			});
		},
		[agent, isDropdownOpen, onViewAgent, sendAnalyticsEvent],
	);

	const handleEdit = useCallback(
		(e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>) => {
			onEditAgent(e, agent.id);
			setIsDropdownOpen(!isDropdownOpen);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgentsEditAgentOption',
				attributes: {
					agentId: agent.id,
				},
			});
		},
		[agent.id, isDropdownOpen, sendAnalyticsEvent, onEditAgent],
	);

	const handleCopy = useCallback(
		(e: any) => {
			onCopyAgent(e, agent.id);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgentsCopyAgentLinkOption',
				attributes: {
					agentId: agent.id,
				},
			});
		},
		[agent.id, sendAnalyticsEvent, onCopyAgent],
	);

	const handleDuplicate = useCallback(
		(e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>) => {
			onDuplicateAgent(e, agent.id);
			setIsDropdownOpen(!isDropdownOpen);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgentsDuplicateAgentOption',
				attributes: {
					agentId: agent.id,
				},
			});
		},
		[agent.id, isDropdownOpen, sendAnalyticsEvent, onDuplicateAgent],
	);

	const handleDelete = useCallback(
		async (e: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>) => {
			onDeleteAgent(e, agent.id);
			setIsDropdownOpen(!isDropdownOpen);
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'browseAgentsDeleteAgentOption',
				attributes: {
					agentId: agent.id,
				},
			});
		},
		[agent.id, isDropdownOpen, sendAnalyticsEvent, onDeleteAgent],
	);

	const handleToggle = useCallback(async () => {
		if (isBEFavouritingAgentsEnabled) {
			await setFavouriteAgent(agent.id, !isStarred);
		} else {
			setFavouriteAgentViaPrefs(agent.id, !isStarred);
		}
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'browseAgentsFavoriteAgentButton',
			attributes: {
				isFavorited: !isStarred,
				agentId: agent.id,
			},
		});
	}, [
		agent.id,
		isBEFavouritingAgentsEnabled,
		isStarred,
		sendAnalyticsEvent,
		setFavouriteAgent,
		setFavouriteAgentViaPrefs,
	]);

	return (
		<Box
			xcss={cardContentStyles}
			onMouseEnter={() => setIsHoveredTrue()}
			onMouseLeave={() => setIsHoveredFalseWithTimeout()}
			onFocus={() => setIsHoveredTrue()}
			onBlur={() => setIsHoveredFalseWithTimeout()}
			testId="agent-card"
		>
			<Pressable xcss={pressableAgentCardStyles} onClick={() => onClickChat(agent)} />
			<Stack grow="fill" space="space.100" spread="space-between">
				<Stack space="space.050">
					<Inline spread="space-between">
						<Stack>
							<Heading size="small">{agent.name}</Heading>
							<Box
								xcss={clickableItemStyles}
								onClick={() => {
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'link',
										actionSubjectId: 'browseAgentsCreatorLink',
										attributes: {
											agentId: agent.id,
										},
									});
								}}
							>
								{formatMessage(messages.createdByLabel, {
									text: (chunks: ReactNode[]) => <Box xcss={createdByTextStyles}>{chunks}</Box>,
									creatorName: <AgentCreatedBy agent={agent} siteId={siteId} simplified />,
								})}
							</Box>
						</Stack>
						<Box
							xcss={toolbarStyles}
							onFocus={setIsHoveredTrue}
							onBlur={setIsHoveredFalseWithTimeout}
						>
							<Box
								testId="agent-card-dropdown-menu"
								xcss={[dropdownIconButtonWrapper, !isFocusedOrHovered && hiddenStyles]}
							>
								<DropdownMenu<HTMLButtonElement>
									isOpen={isDropdownOpen}
									testId="agent-card-dropdown-menu-button"
									trigger={({ triggerRef, ...props }) => (
										<IconButton
											{...props}
											onClick={() => {
												setIsDropdownOpen(!isDropdownOpen);
												sendAnalyticsEvent({
													action: 'clicked',
													actionSubject: 'button',
													actionSubjectId: 'browseAgentsOptionsButton',
													attributes: {
														agentId: agent.id,
													},
												});
											}}
											spacing="compact"
											appearance="subtle"
											icon={MoreIcon}
											label={formatMessage(messages.moreActionsLabel)}
											ref={triggerRef}
										/>
									)}
								>
									<DropdownItemGroup>
										<DropdownItem testId="agent-card-dropdown-view" onClick={handleViewAgent}>
											{formatMessage(messages.viewAgent)}
										</DropdownItem>

										<DropdownItem testId="agent-card-dropdown-duplicate" onClick={handleDuplicate}>
											{formatMessage(messages.duplicateAgent)}
										</DropdownItem>
										<DropdownItem testId="agent-card-dropdown-copy" onClick={handleCopy}>
											{formatMessage(messages.copyLinkToProfile)}
										</DropdownItem>
									</DropdownItemGroup>
									{agentIsCreatedByUser ? (
										<DropdownItemGroup hasSeparator>
											<DropdownItem testId="agent-card-dropdown-edit" onClick={handleEdit}>
												{formatMessage(messages.editAgent)}
											</DropdownItem>
											<DropdownItem testId="agent-card-dropdown-delete" onClick={handleDelete}>
												{formatMessage(messages.deleteAgent)}
											</DropdownItem>
										</DropdownItemGroup>
									) : null}
								</DropdownMenu>
							</Box>
							<StarIconButton
								isStarred={isStarred}
								handleToggle={handleToggle}
								visible={isStarred || isFocusedOrHovered}
							/>
						</Box>
					</Inline>
					<Text>{agent.description}</Text>
				</Stack>
			</Stack>
		</Box>
	);
};
